<template>
  <div v-editable="blok">
    <div class="page-mx module-pt module-pb">
      <div class="flex flex-col items-center justify-center clamp-gap-8-12">
        <h1
          class="text-display-md"
          :style="{ 'font-weight': `var(--fw-headline-md)` }">
          {{ blok.title }}
        </h1>
        <ElementSearchBar
          class="pointer-events-auto w-full clamp-max-w-75-150"
          v-model="searchTerm"
          :searchPlaceholder="blok.searchPlaceholder"
          submitText=""
          @search="doSearch"
          @clear="doClearSearch" />
      </div>
      <div class="clamp-mt-8-12">
        <div>
          <ElementArticleGrid
            :stories="stories"
            :skeletonCardCount="stories?.length || 12"
            :loading="state === 'LOADING'" />
          <ElementPagination
            v-if="state === 'RESULTS'"
            :totalItems="totalItems"
            :postsPerPage="postsPerPage"
            class="clamp-pt-8-12" />
        </div>
        <div
          v-if="state === 'NO_RESULTS'"
          class="flex items-center justify-center">
          <ElementErrorNoResults
            :blok="blok.errorNoResults[0]"
            @clear="doClearSearch"
            class="w-full" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TemplateArticleOverviewStoryblok } from "@/types";
import useArticles from "@/composables/useArticles";
import usePagination from "@/composables/usePagination";

interface Props {
  blok: TemplateArticleOverviewStoryblok;
}

defineProps<Props>();

const { stories, totalItems, fetchArticles, state } = useArticles();
const router = useRouter();
const emits = defineEmits();

const searchTerm = ref("");
const lastSearchTerm = ref("");
const postsPerPage = 12; // should be dividable by 3 AND 2 so that the layout supports it and there is no extra empty space

const pagination = usePagination({
  postsPerPage: postsPerPage,
  totalItems: totalItems,
  router: router,
  emit: emits,
  pageQueryName: "page",
});
const routerSearchTerm = computed(
  () => (router.currentRoute.value.query.s as string) || undefined
);
const doSearch = () => {
  state.value = "LOADING";
  const searchTermValue = searchTerm.value.trim();
  if (searchTermValue === "") {
    searchTerm.value = "";
    updateResults(pagination.currentPage.value, false);
  } else {
    // Check if the search term is already in the router
    if (router.currentRoute.value.query.s !== searchTermValue) {
      lastSearchTerm.value = searchTermValue;
      router.push({
        query: {
          ...router.currentRoute.value.query,
          s: searchTermValue,
          page: 1,
        },
      });
    } else {
      // If the search term is already in the router, update the search term value
      // and call updateResults directly
      searchTerm.value = searchTermValue;
      updateResults(pagination.currentPage.value, false);
    }
  }
};

const doClearSearch = () => {
  searchTerm.value = "";
  router.push({
    query: {
      page: 1,
      s: undefined,
    },
  });
};

const updateResults = async (page: number, delay: boolean) => {
  const s = (router.currentRoute.value.query.s as string | undefined) || "";
  searchTerm.value = s;
  fetchArticles({
    postsPerPage: postsPerPage,
    currentPage: page,
    searchTerm: s,
    delay: delay,
  });
};
watch(
  () => routerSearchTerm.value,
  (newValue) => {
    updateResults(pagination.currentPage.value, true);
  }
);
watch(
  () => pagination.currentPage.value,
  (newValue) => {
    const currentPage = newValue;
    updateResults(currentPage, true);
  }
);

onMounted(() => {
  updateResults(pagination.currentPage.value, false);
  lastSearchTerm.value =
    (router.currentRoute.value.query.s as string | undefined) || "";
});
</script>

<style scoped lang="scss"></style>
